export enum AnalyticsEvent {
    LogIn = 'log-in',
    LogOut = 'log-out',
    IssuedItemsCreated = 'issued-item-created',
    Transfer = 'transfer-approval',
    StopPayment = 'stop-payment-approval',
    AchPayment = 'ach-payment',
    AchTaxPayment = 'ach-tax-payment',
    WirePayment = 'wire-payment',
    LoanPayment = 'loan-payment',
    BalanceReport = 'balance-report',
    TransactionReport = 'transaction-report',
    CheckExceptionReview = 'check-exception-review',
    GetElectronicDocs = 'get-electronic-documents',
    AchExceptionDecision = 'ach-exception-decision',
    AchFilterCreated = 'ach-filter-created',
}

type IrDateType = 'Current Day' | 'Prior Day';

export type AnalyticsEventPayloads = {
    [AnalyticsEvent.LogIn]: undefined;
    [AnalyticsEvent.LogOut]: undefined;
    [AnalyticsEvent.BalanceReport]: {
        dateType: IrDateType;
        count: number;
        totalCheckingAmount: number;
        totalSavingsAmount: number;
    };
    [AnalyticsEvent.TransactionReport]: {
        dateType: IrDateType;
        count: number;
        totalCreditAmount: number;
        totalDebitAmount: number;
    };
    [AnalyticsEvent.Transfer]: {
        amount: number;
        transactionId: string;
        status: string;
        startDate?: string;
        endDate?: string;
        frequency?: string;
    };
    [AnalyticsEvent.StopPayment]: {
        id: string;
        accountId: string;
        status: string;
        checkAmount: number;
        approvalsRequiredCount: number;
    };
    [AnalyticsEvent.AchExceptionDecision]: {
        amount: number;
        decision: 'pay' | 'return' | 'none';
        exceptionType: 'ACH Debit' | 'ACH Credit';
        secCode: 'PPD' | 'CCD';
        accountType: 'Checking' | 'Savings';
    };
    [AnalyticsEvent.AchFilterCreated]: {
        fromAmount?: number;
        toAmount?: number;
        isRange: boolean;
        secCode?:
            | 'ARC'
            | 'BOC'
            | 'CCD'
            | 'CIE'
            | 'CTX'
            | 'IAT'
            | 'POP'
            | 'POS'
            | 'PPD'
            | 'RCK'
            | 'TEL'
            | 'WEB'
            | 'XCK';
        transactionType: 'Credit' | 'Debit';
        allowType: 'Allow' | 'Disallow';
    };
    [AnalyticsEvent.AchPayment]: {
        companyId: string;
        transactionId: string;
        creditAmount: number;
        debitAmount: number;
        status: string;
    };
    [AnalyticsEvent.AchTaxPayment]: {
        taxType: 'state' | 'federal';
        amount: number;
        amountType: number;
    };
    [AnalyticsEvent.WirePayment]: {
        amount: number;
        isInternational: boolean;
        isDli?: boolean;
        currency: string;
        frequency: string;
    };
    [AnalyticsEvent.LoanPayment]: {
        amount: number;
        type: 'regular' | 'principal';
    };
    [AnalyticsEvent.CheckExceptionReview]: {
        itemCount: number;
        totalAmount: number;
    };
    [AnalyticsEvent.IssuedItemsCreated]: {
        itemCount: number;
        totalAmount: number;
    };
    [AnalyticsEvent.GetElectronicDocs]: {
        product: 'Wausau' | 'Esi' | 'PrintMail' | 'none';
        report: string;
    };
};

export interface UserIdentificationData {
    userId: number;
    isAdmin: boolean;
    isSuperUser: boolean;
    companyId: string;
}

export interface IAnalyticsService {
    init(): void;
    identify(data: UserIdentificationData): void;
    reset(): void;
    track<Event extends AnalyticsEvent>(
        eventName: Event,
        // have to use spread syntax to support the optional parameter on conditional types
        // https://stackoverflow.com/a/52318137/574930
        ...payload: AnalyticsEventPayloads[Event] extends object
            ? [AnalyticsEventPayloads[Event]]
            : [never?]
    ): void;
    trackPageView(route: string): void;
}
